import { useCallback } from 'react';
import { useAuth } from '../auth/hooks/useAuth';
import type { LineId, PermissionAction, ResolvedPermissions } from './permissionTypes';

export interface UsePermissions {
  /**
   * True only when the loaded rights grant `action` on `line`. Refused by default:
   * false while permissions are not loaded (`null`) and false for any absent line.
   * No hierarchy walk — the backend already flattens (GET /auth/me).
   */
  can: (line: LineId, action: PermissionAction) => boolean;
  permissions: ResolvedPermissions | null;
  /** True once GET /auth/me has answered (even with `{}`); false before / on error. */
  isLoaded: boolean;
  refreshPermissions: () => Promise<void>;
}

/**
 * Reads the connected user's flat effective rights from the auth context and
 * exposes `can(line, action)`. Single source of truth: the AuthProvider reducer.
 */
export function usePermissions(): UsePermissions {
  const { permissions, refreshPermissions } = useAuth();

  const can = useCallback(
    (line: LineId, action: PermissionAction): boolean =>
      permissions?.[line]?.includes(action) ?? false,
    [permissions],
  );

  return { can, permissions, isLoaded: permissions !== null, refreshPermissions };
}
